import type { AnalyticsInsightAccount } from '@/types/api';

export interface InsightComparison {
  selected: number | null;
  rest: number | null;
  ratio: number | null;
}

export interface ConcentrationPoint {
  count: number;
  viewsShare: number;
  equalShare: number;
}

function sum(accounts: AnalyticsInsightAccount[], key: 'viewsDelta' | 'measuredReels') {
  return accounts.reduce((total, account) => total + account[key], 0);
}

function divide(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function comparison(selected: number | null, rest: number | null): InsightComparison {
  return {
    selected,
    rest,
    ratio: selected !== null && rest !== null && rest > 0 ? selected / rest : null,
  };
}

export function buildAnalyticsInsights(population: AnalyticsInsightAccount[], threshold = 80) {
  const accounts = population
    .filter((account) => account.measurable)
    .sort((a, b) => b.viewsDelta - a.viewsDelta || a.accountId.localeCompare(b.accountId));
  const excludedAccounts = population.filter((account) => !account.measurable);
  const totalViews = sum(accounts, 'viewsDelta');
  const target = (Math.min(100, Math.max(0, threshold)) / 100) * totalViews;
  let selectedViews = 0;
  let count = 0;
  while (count < accounts.length && selectedViews < target) {
    selectedViews += accounts[count].viewsDelta;
    count += 1;
  }
  const selected = accounts.slice(0, count);
  const rest = accounts.slice(count);
  const points: ConcentrationPoint[] = [];
  if (totalViews > 0) {
    points.push({ count: 0, viewsShare: 0, equalShare: 0 });
    let cumulative = 0;
    accounts.forEach((account, index) => {
      cumulative += account.viewsDelta;
      points.push({
        count: index + 1,
        viewsShare: (cumulative / totalViews) * 100,
        equalShare: ((index + 1) / accounts.length) * 100,
      });
    });
  }
  const knownFollowers = (cohort: AnalyticsInsightAccount[]) =>
    cohort.flatMap((account) =>
      account.latestFollowersCount === null ? [] : [account.latestFollowersCount],
    );
  const selectedFollowers = knownFollowers(selected);
  const restFollowers = knownFollowers(rest);
  return {
    accounts,
    excludedAccounts,
    totalViews,
    selected,
    rest,
    selectedViews,
    selectedShare: divide(selectedViews, totalViews),
    selectedAccountShare: divide(selected.length, accounts.length),
    points,
    comparisons: {
      viewsPerReel: comparison(
        divide(selectedViews, sum(selected, 'measuredReels')),
        divide(sum(rest, 'viewsDelta'), sum(rest, 'measuredReels')),
      ),
      reelsPerAccount: comparison(
        divide(sum(selected, 'measuredReels'), selected.length),
        divide(sum(rest, 'measuredReels'), rest.length),
      ),
      followers: {
        ...comparison(median(selectedFollowers), median(restFollowers)),
        selectedKnown: selectedFollowers.length,
        restKnown: restFollowers.length,
      },
    },
  };
}

export type AnalyticsInsightModel = ReturnType<typeof buildAnalyticsInsights>;
